import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import axios from 'axios'
import { apiEndpoint } from 'configs'

const initialState = {
  team: [],
  teamLoading: false,
  teamError: null,

  authLoading: false,
  authError: null,
  employeeToken: null,
  selectedEmployee: null,

  attendanceData: [],
  attendanceLoading: false,
  attendanceError: null,
  attendanceCurrentPage: 0,
  attendanceTotalPages: 0,
  attendanceHasMore: false,

  checkinStatus: null,
  notesStatus: null,
}

export const fetchTeam = createAsyncThunk(
  'centerAttendance/fetchTeam',
  async (
    { page = 0, size = 10, sortby = 'createdAt', sortdir = 'ASC', s = '' },
    { rejectWithValue, getState },
  ) => {
    try {
      const { auth } = getState()
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
      }
      let url = `${apiEndpoint}/api/v1/bussines/team?sortby=${sortby}&sortdir=${sortdir}&page=${page}&size=${size}`
      if (s) {
        url += `&s=${encodeURIComponent(s)}`
      }
      const response = await axios.get(url, config)
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch team',
      )
    }
  },
)

export const authFaceRecognition = createAsyncThunk(
  'centerAttendance/authFaceRecognition',
  async ({ photo, employeeUserId }, { rejectWithValue, getState }) => {
    try {
      const { auth } = getState()
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
        },
      }
      const response = await axios.post(
        `${apiEndpoint}/api/v1/bussines/center/authfacerecognition`,
        { photo, employeeUserId },
        config,
      )
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Face recognition failed',
      )
    }
  },
)

export const fetchCenterAttendance = createAsyncThunk(
  'centerAttendance/fetchCenterAttendance',
  async ({ page = 0, size = 9999 }, { rejectWithValue, getState }) => {
    try {
      const { auth, centerAttendance } = getState()
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
          employeeToken: centerAttendance.employeeToken,
        },
      }
      const response = await axios.get(
        `${apiEndpoint}/api/v1/bussines/center/userattendance/list?page=${page}&size=${size}`,
        config,
      )
      
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to fetch attendance',
      )
    }
  },
)

export const checkInOrOutCenter = createAsyncThunk(
  'centerAttendance/checkInOrOutCenter',
  async (payload, { rejectWithValue, getState }) => {
    try {
      const { auth, centerAttendance } = getState()
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
          employeeToken: centerAttendance.employeeToken,
        },
      }
      const response = await axios.post(
        `${apiEndpoint}/api/v1/bussines/center/userattendance/checkinorout`,
        payload,
        config,
      )
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to check in/out',
      )
    }
  },
)

export const setCenterAttendanceNotes = createAsyncThunk(
  'centerAttendance/setNotes',
  async ({ notes }, { rejectWithValue, getState }) => {
    try {
      const { auth, centerAttendance } = getState()
      const config = {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${auth.token}`,
          employeeToken: centerAttendance.employeeToken,
        },
      }
      const response = await axios.post(
        `${apiEndpoint}/api/v1/bussines/center/userattendance/setnotes`,
        { notes },
        config,
      )
      return response.data
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || 'Failed to set notes',
      )
    }
  },
)

const centerAttendanceSlice = createSlice({
  name: 'centerAttendance',
  initialState,
  reducers: {
    resetState: (state) => {
      state.employeeToken = null
      state.selectedEmployee = null
      state.attendanceData = []
      state.attendanceCurrentPage = 0
      state.attendanceTotalPages = 0
      state.attendanceHasMore = false
      state.checkinStatus = null
      state.notesStatus = null
    },
    setSelectedEmployee: (state, action) => {
      state.selectedEmployee = action.payload
    },
    setEmployeeToken: (state, action) => {
      state.employeeToken = action.payload
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Team
      .addCase(fetchTeam.pending, (state) => {
        state.teamLoading = true
        state.teamError = null
      })
      .addCase(fetchTeam.fulfilled, (state, action) => {
        state.teamLoading = false
        state.team = action.payload.items || []
      })
      .addCase(fetchTeam.rejected, (state, action) => {
        state.teamLoading = false
        state.teamError = action.payload
      })

      // Auth Face Recognition
      .addCase(authFaceRecognition.pending, (state) => {
        state.authLoading = true
        state.authError = null
      })
      .addCase(authFaceRecognition.fulfilled, (state, action) => {
        state.authLoading = false
        state.employeeToken = action.payload.employeeToken
      })
      .addCase(authFaceRecognition.rejected, (state, action) => {
        state.authLoading = false
        state.authError = action.payload
      })

      // Fetch Center Attendance
      .addCase(fetchCenterAttendance.pending, (state) => {
        state.attendanceLoading = true
        state.attendanceError = null
      })
      .addCase(fetchCenterAttendance.fulfilled, (state, action) => {
        state.attendanceLoading = false
        const newItems = action.payload.items || []
        const currentPage = action.payload.currentPage || 0
        const totalPages = action.payload.totalPages || 0
        
        // If page is 0, replace data; otherwise append
        if (currentPage === 0) {
          state.attendanceData = newItems
        } else {
          state.attendanceData = [...state.attendanceData, ...newItems]
        }
        
        state.attendanceCurrentPage = currentPage
        state.attendanceTotalPages = totalPages
        state.attendanceHasMore = currentPage + 1 < totalPages
      })
      .addCase(fetchCenterAttendance.rejected, (state, action) => {
        state.attendanceLoading = false
        state.attendanceError = action.payload
      })

      // Check In/Out
      .addCase(checkInOrOutCenter.pending, (state) => {
        state.checkinStatus = 'loading'
      })
      .addCase(checkInOrOutCenter.fulfilled, (state) => {
        state.checkinStatus = 'success'
      })
      .addCase(checkInOrOutCenter.rejected, (state, action) => {
        state.checkinStatus = `error: ${action.payload}`
      })

      // Set Notes
      .addCase(setCenterAttendanceNotes.pending, (state) => {
        state.notesStatus = 'loading'
      })
      .addCase(setCenterAttendanceNotes.fulfilled, (state) => {
        state.notesStatus = 'success'
      })
      .addCase(setCenterAttendanceNotes.rejected, (state, action) => {
        state.notesStatus = `error: ${action.payload}`
      })
  },
})

export const { resetState, setSelectedEmployee, setEmployeeToken } =
  centerAttendanceSlice.actions
export default centerAttendanceSlice.reducer
